import { ref, watch, onMounted, onUnmounted, type Ref } from 'vue'

import { useCarousel } from './use-carousel'

export interface UseCarouselDotsType {
  selectedIndex: Ref<number>,
  scrollSnaps: Ref<number[]>,
  onDotButtonClick: (index: number) => void
}

export function useCarouselDots (): UseCarouselDotsType {
  const { carouselApi } = useCarousel()

  const selectedIndex = ref(0)
  const scrollSnaps = ref<number[]>([])

  const onDotButtonClick = (index: number) => {
    if (!carouselApi.value) return
    carouselApi.value.scrollTo(index)
  }

  type EmblaType = NonNullable<(typeof carouselApi)['value']>

  const onInit = (embla: EmblaType) => {
    scrollSnaps.value = embla.scrollSnapList()
    selectedIndex.value = embla.selectedScrollSnap()
  }

  const onSelect = (embla: EmblaType) => {
    selectedIndex.value = embla.selectedScrollSnap()
  }

  const setupEmblaListeners = () => {
    if (!carouselApi.value) return

    onInit(carouselApi.value)
    onSelect(carouselApi.value)

    carouselApi.value.on('reInit', onInit)
    carouselApi.value.on('reInit', onSelect)
    carouselApi.value.on('select', onSelect)
  }

  const removeEmblaListeners = () => {
    if (!carouselApi.value) return

    carouselApi.value.off('reInit', onInit)
    carouselApi.value.off('reInit', onSelect)
    carouselApi.value.off('select', onSelect)
  }

  onMounted(() => {
    setupEmblaListeners()
  })

  onUnmounted(() => {
    removeEmblaListeners()
  })

  watch(carouselApi, (newApi, oldApi) => {
    if (oldApi) {
      removeEmblaListeners()
    }
    if (newApi) {
      setupEmblaListeners()
    }
  })

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  }
}
